import { useEffect, useState } from "react";
import { getTodaysBills } from "../../services/orderService";

// Today's settled bills. The cashier opens one to correct an item that was rung
// up twice or missed, then reprints it.
//
// `salon` swaps the "where" column (table / counter) for the customer, which is
// how a salon's front desk finds a bill.
//
// `onWhatsApp` (optional) adds a WhatsApp button to bills with a customer mobile.
function BillsHistory({ onOpenBill, onWhatsApp, salon = false }) {

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const load = async () => {
        try {
            const res = await getTodaysBills();
            setBills(res.data.data || []);
        } catch (e) {
            console.error("Bills load error:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Reload whenever a bill closes, so a corrected total shows straight away.
    useEffect(() => {
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    const whereOf = (b) => salon
        ? (b.customer_name || "Walk-in")
        : (b.table_name ? `Table ${b.table_name}` : (b.no_kitchen ? "Bill Only" : "Counter"));

    const term = search.trim().toLowerCase();
    const filtered = bills.filter((b) =>
        !term ||
        (b.order_number || "").toLowerCase().includes(term) ||
        whereOf(b).toLowerCase().includes(term) ||
        (salon && (b.customer_mobile || "").includes(term))
    );

    const isCancelled = (b) => b.order_status === "Cancelled";

    // A cancelled bill keeps its number (it is never reused) but took no money,
    // so it is listed for the audit trail and left out of both counts.
    const settled = filtered.filter((b) => !isCancelled(b));
    const totalTaken = settled.reduce((s, b) => s + Number(b.paid_amount || 0), 0);
    const cancelledCount = filtered.length - settled.length;

    const timeOf = (iso) =>
        new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    return (
        <div className="menuview">

            <div className="menuavail-head">
                <h2>🧾 Bills · Today</h2>
                <input
                    className="menuavail-search"
                    placeholder={salon ? "Search bill no., customer or mobile…" : "Search bill no. or table…"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className="bills-summary">
                    <b>{settled.length}</b> bills · <b>₹{totalTaken.toFixed(2)}</b>
                    {cancelledCount > 0 && <> · <b>{cancelledCount}</b> cancelled</>}
                </span>
            </div>

            <div className="bills-body">
                {loading ? (
                    <p className="menuavail-empty">Loading…</p>
                ) : filtered.length === 0 ? (
                    <p className="menuavail-empty">
                        {bills.length === 0
                            ? "No bills settled today yet."
                            : "No bill matches that search."}
                    </p>
                ) : (
                    <table className="bills-table">
                        <thead>
                            <tr>
                                <th>Bill No.</th>
                                <th>{salon ? "Customer" : "Where"}</th>
                                {salon && <th>Stylist</th>}
                                <th>Time</th>
                                <th className="num">Items</th>
                                <th>Paid via</th>
                                <th className="num">Total</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((b) => {
                                // "edited" shows ONLY when the bill's ITEMS were
                                // changed (qty/add/remove), not for a payment-mode
                                // change or a plain reprint. Driven by the synced
                                // orders.item_edited flag.
                                const corrected = Number(b.item_edited) > 0;
                                const cancelled = isCancelled(b);
                                return (
                                    <tr key={b.id} className={cancelled ? "bills-row-cancelled" : ""}>
                                        <td className="bills-no">{b.order_number}</td>
                                        <td>{whereOf(b)}</td>
                                        {salon && <td>{b.stylist_name || "—"}</td>}
                                        <td>{timeOf(b.created_at)}</td>
                                        <td className="num">{Number(b.item_count)}</td>
                                        <td>{cancelled ? "—" : (b.payment_method || "—")}</td>
                                        <td className="num bills-amt">
                                            {cancelled ? "₹0.00" : `₹${Number(b.grand_total).toFixed(2)}`}
                                            {cancelled && <span className="bills-tag bills-tag-cancelled">cancelled</span>}
                                            {!cancelled && corrected && <span className="bills-tag">edited</span>}
                                        </td>
                                        <td className="num">
                                            {/* A cancelled bill took no money — there is
                                                nothing to correct or reprint, and its
                                                number stays burnt. */}
                                            {cancelled ? (
                                                <span className="bills-void">No bill</span>
                                            ) : (
                                                <>
                                                    {onWhatsApp && b.customer_mobile && (
                                                        <button
                                                            className="bills-wa"
                                                            onClick={() => onWhatsApp(b)}
                                                            title={`Send this bill to ${b.customer_mobile} on WhatsApp`}
                                                        >
                                                            WhatsApp
                                                        </button>
                                                    )}
                                                    <button
                                                        className="bills-open"
                                                        onClick={() => onOpenBill(b)}
                                                    >
                                                        View / Edit
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

        </div>
    );
}

export default BillsHistory;
